import { useEffect, useRef, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { LETTERS, shuffle, randomFrom } from "@/lib/gameData";
import { GAME_FEEDBACK_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function newRound() {
  const target = randomFrom(LETTERS);
  const distractors = shuffle(LETTERS.filter((l) => l !== target)).slice(0, 3);
  const options = shuffle([target, ...distractors]);
  return { target, options };
}

export default function CaseMatch() {
  const [round, setRound] = useState(newRound);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [locked, setLocked] = useState(false);
  const timersRef = useRef([]);
  const { award } = useAuth();

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  useEffect(() => {
    setFeedback(null);
    setLocked(false);
    const t = setTimeout(() => speak(round.target, { rate: 0.85 }), 200);
    timersRef.current.push(t);
  }, [round]);

  const pick = (letter) => {
    if (locked) return;
    setLocked(true);
    setAttempts((a) => a + 1);
    if (letter === round.target) {
      setScore((s) => s + 1);
      award(10, "case");
      setFeedback({ correct: true, letter });
      toast.success(`Yes! Uppercase ${round.target} matches lowercase ${letter.toLowerCase()}.`);
      const t = setTimeout(() => setRound(newRound()), GAME_FEEDBACK_MS);
      timersRef.current.push(t);
    } else {
      setFeedback({ correct: false, letter });
      toast.error(`Not quite — try again.`);
      const t = setTimeout(() => {
        setFeedback(null);
        setLocked(false);
      }, GAME_FEEDBACK_MS);
      timersRef.current.push(t);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div
          data-testid="case-score"
          className="inline-flex items-center gap-2 bg-pink-50 text-pink-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid="case-next"
          onClick={() => setRound(newRound())}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New pair
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
          Find the matching lowercase letter
        </p>
        <button
          data-testid="case-target"
          onClick={() => speak(round.target, { rate: 0.85 })}
          className="mt-2 font-display text-8xl sm:text-9xl font-semibold text-pink-500 leading-none phonx-letter-pop"
        >
          {round.target}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {round.options.map((letter, i) => {
          const lower = letter.toLowerCase();
          const isRight = feedback && feedback.correct && feedback.letter === letter;
          const isWrong = feedback && !feedback.correct && feedback.letter === letter;
          return (
            <button
              key={letter}
              data-testid={`case-option-${i}`}
              onClick={() => pick(letter)}
              disabled={locked}
              className={`aspect-square rounded-3xl border-2 font-display text-6xl font-semibold transition-all
                ${isRight ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop" : ""}
                ${isWrong ? "bg-rose-100 border-rose-300 text-rose-600 phonx-shake" : ""}
                ${!feedback ? "bg-white border-slate-200 text-slate-800 hover:border-pink-400 hover:text-pink-500 hover:-translate-y-1 shadow-sm hover:shadow-lg" : ""}
              `}
            >
              {lower}
            </button>
          );
        })}
      </div>
    </div>
  );
}
